import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { Save, Trash2, ArrowLeft, Loader2, Plus, X, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { getBundle, saveBundle, deleteBundle, getAllProducts } from '../../../services/firestore/formularyService';
import type { Bundle, BundleProduct, BundleTrigger, ProductStatus, Product } from '../../../types/formulary';
import type { ClinicalTier, HealthGoal } from '../../../types/index';

const EMPTY_TRIGGER: BundleTrigger = {
  conditions: [],
  goalConditions: [],
  minimumMatchCount: 1,
  description: '',
};

const EMPTY_BUNDLE: Bundle = {
  id: '',
  bundleNumber: 0,
  name: '',
  tier: 1,
  target: '',
  description: '',
  products: [],
  trigger: { ...EMPTY_TRIGGER },
  pillsPerDay: '',
  priceRange: '',
  upsellPath: '',
  upsellBundleId: '',
  contraindications: [],
  warnings: [],
  requiresProvider: false,
  checkoutNote: '',
  gender: 'any',
  evidenceLevel: 'moderate',
  status: 'draft',
  sortOrder: 0,
  updatedAt: '',
  updatedBy: 'admin',
};

const STATUSES: ProductStatus[] = ['active', 'draft', 'discontinued'];
const GENDERS: Array<'female' | 'male' | 'any'> = ['female', 'male', 'any'];
const EVIDENCE_LEVELS: Array<'strong' | 'moderate' | 'emerging'> = ['strong', 'moderate', 'emerging'];
const OPERATORS: Array<'lt' | 'gt' | 'eq' | 'between'> = ['lt', 'gt', 'eq', 'between'];
const HEALTH_GOALS: HealthGoal[] = [
  'energy', 'sleep', 'cognition', 'longevity', 'body_composition',
  'hormonal', 'cardiovascular', 'immune', 'sexual_health', 'prostate',
];

export function BundleForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;

  const [bundle, setBundle] = useState<Bundle>({ ...EMPTY_BUNDLE });
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [productSearch, setProductSearch] = useState('');
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['basic', 'products', 'trigger', 'safety', 'checkout', 'meta'])
  );

  useEffect(() => {
    loadData();
  }, [id]);

  async function loadData() {
    try {
      setLoading(true);
      const products = await getAllProducts();
      setAllProducts(products);

      if (id) {
        const data = await getBundle(id);
        if (data) {
          setBundle(data);
        } else {
          setToast('Bundle not found');
          navigate('/admin/bundles');
        }
      }
    } catch (err) {
      setToast('Error loading: ' + (err instanceof Error ? err.message : 'Unknown'));
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    if (!bundle.id.trim()) {
      setToast('Bundle ID is required');
      return;
    }
    if (!bundle.name.trim()) {
      setToast('Bundle name is required');
      return;
    }
    try {
      setSaving(true);
      await saveBundle(bundle);
      setToast('Bundle saved successfully');
      if (isNew) {
        navigate(`/admin/bundles/${bundle.id}`, { replace: true });
      }
    } catch (err) {
      setToast('Error saving: ' + (err instanceof Error ? err.message : 'Unknown'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!id) return;
    if (!window.confirm(`Delete "${bundle.name}"? This cannot be undone.`)) return;
    try {
      setDeleting(true);
      await deleteBundle(id);
      setToast('Bundle deleted');
      navigate('/admin/bundles');
    } catch (err) {
      setToast('Error deleting: ' + (err instanceof Error ? err.message : 'Unknown'));
      setDeleting(false);
    }
  }

  function toggleSection(section: string) {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  }

  function updateField<K extends keyof Bundle>(key: K, value: Bundle[K]) {
    setBundle((prev) => ({ ...prev, [key]: value }));
  }

  function parseArray(value: string): string[] {
    return value.split(',').map((s) => s.trim()).filter(Boolean);
  }

  // Product picker
  function addProductToBundle(product: Product) {
    const exists = bundle.products.some((bp) => bp.productId === product.id);
    if (exists) return;
    const bp: BundleProduct = {
      productId: product.id,
      productName: product.name,
      dosageOverride: '',
      isOptional: false,
      sortOrder: bundle.products.length,
    };
    setBundle((prev) => ({ ...prev, products: [...prev.products, bp] }));
  }

  function removeProductFromBundle(productId: string) {
    setBundle((prev) => ({
      ...prev,
      products: prev.products.filter((bp) => bp.productId !== productId),
    }));
  }

  function updateBundleProduct(index: number, field: keyof BundleProduct, value: string | boolean | number) {
    setBundle((prev) => {
      const products = [...prev.products];
      products[index] = { ...products[index], [field]: value };
      return { ...prev, products };
    });
  }

  // Trigger conditions
  function addTriggerCondition() {
    setBundle((prev) => ({
      ...prev,
      trigger: {
        ...prev.trigger,
        conditions: [...prev.trigger.conditions, { biomarker: '', operator: 'gt' as const, value: 0 }],
      },
    }));
  }

  function updateTriggerCondition(index: number, field: string, value: string | number) {
    setBundle((prev) => {
      const conditions = [...prev.trigger.conditions];
      conditions[index] = { ...conditions[index], [field]: value };
      return { ...prev, trigger: { ...prev.trigger, conditions } };
    });
  }

  function removeTriggerCondition(index: number) {
    setBundle((prev) => ({
      ...prev,
      trigger: {
        ...prev.trigger,
        conditions: prev.trigger.conditions.filter((_, i) => i !== index),
      },
    }));
  }

  const availableProducts = allProducts.filter(
    (p) =>
      !bundle.products.some((bp) => bp.productId === p.id) &&
      (!productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-gold" size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      {/* Toast */}
      {toast && (
        <div
          className="fixed top-4 right-4 z-50 bg-bg-elevated border border-bg-tertiary rounded-lg px-4 py-3 text-base shadow-lg flex items-center gap-2 anim-fade-in-down"
        >
          <Check size={16} className="text-optimal" />
          {toast}
          <button onClick={() => setToast(null)} className="ml-2 text-text-muted hover:text-text-primary">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/bundles')} className="p-2 rounded-lg hover:bg-bg-elevated text-text-secondary">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h2 className="text-3xl font-display text-gold">{isNew ? 'New Bundle' : 'Edit Bundle'}</h2>
            {!isNew && <p className="text-base text-text-muted">ID: {bundle.id}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-base font-medium transition-colors disabled:opacity-50"
            >
              {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
              Delete
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-accent hover:bg-accent/80 text-white rounded-lg text-base font-medium transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save Bundle
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {/* Basic Info */}
        <FormSection title="Basic Info" id="basic" expanded={expandedSections.has('basic')} onToggle={toggleSection}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Bundle ID" required>
              <input type="text" value={bundle.id} onChange={(e) => updateField('id', e.target.value)} disabled={!isNew} placeholder="e.g. foundation-essentials" className="input-field" />
            </Field>
            <Field label="Bundle Number">
              <input type="number" value={bundle.bundleNumber} onChange={(e) => updateField('bundleNumber', Number(e.target.value))} className="input-field" />
            </Field>
            <Field label="Name" required className="sm:col-span-2">
              <input type="text" value={bundle.name} onChange={(e) => updateField('name', e.target.value)} placeholder="Bundle name" className="input-field" />
            </Field>
            <Field label="Description" className="sm:col-span-2">
              <textarea value={bundle.description} onChange={(e) => updateField('description', e.target.value)} placeholder="Bundle description..." rows={2} className="input-field" />
            </Field>
            <Field label="Target">
              <input type="text" value={bundle.target} onChange={(e) => updateField('target', e.target.value)} placeholder="e.g. Every patient baseline" className="input-field" />
            </Field>
            <Field label="Tier">
              <select value={bundle.tier} onChange={(e) => updateField('tier', Number(e.target.value) as ClinicalTier)} className="input-field">
                <option value={1}>Tier 1</option>
                <option value={2}>Tier 2</option>
                <option value={3}>Tier 3</option>
                <option value={4}>Tier 4</option>
              </select>
            </Field>
            <Field label="Gender">
              <select value={bundle.gender} onChange={(e) => updateField('gender', e.target.value as 'female' | 'male' | 'any')} className="input-field">
                {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </Field>
            <Field label="Evidence Level">
              <select value={bundle.evidenceLevel} onChange={(e) => updateField('evidenceLevel', e.target.value as 'strong' | 'moderate' | 'emerging')} className="input-field">
                {EVIDENCE_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={bundle.status} onChange={(e) => updateField('status', e.target.value as ProductStatus)} className="input-field">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Price Range">
              <input type="text" value={bundle.priceRange} onChange={(e) => updateField('priceRange', e.target.value)} placeholder="e.g. $149/mo" className="input-field" />
            </Field>
            <Field label="Pills Per Day">
              <input type="text" value={bundle.pillsPerDay || ''} onChange={(e) => updateField('pillsPerDay', e.target.value)} placeholder="e.g. 7-8" className="input-field" />
            </Field>
          </div>
        </FormSection>

        {/* Products */}
        <FormSection title={`Products (${bundle.products.length})`} id="products" expanded={expandedSections.has('products')} onToggle={toggleSection}>
          <div className="space-y-4">
            {/* Current products */}
            {bundle.products.length > 0 && (
              <div className="space-y-2">
                {bundle.products.map((bp, idx) => (
                  <div key={bp.productId} className="flex items-center gap-3 bg-bg-elevated border border-bg-tertiary rounded-lg p-3">
                    <span className="text-sm text-text-muted w-6">{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-medium text-text-primary truncate">{bp.productName}</p>
                      <p className="text-sm text-text-muted">{bp.productId}</p>
                    </div>
                    <input
                      type="text"
                      value={bp.dosageOverride || ''}
                      onChange={(e) => updateBundleProduct(idx, 'dosageOverride', e.target.value)}
                      placeholder="Dosage override"
                      className="input-field text-sm w-36 hidden sm:block"
                    />
                    <label className="flex items-center gap-1.5 cursor-pointer text-sm text-text-secondary">
                      <input
                        type="checkbox"
                        checked={bp.isOptional}
                        onChange={(e) => updateBundleProduct(idx, 'isOptional', e.target.checked)}
                        className="w-3.5 h-3.5 rounded border-bg-tertiary bg-bg-elevated text-accent"
                      />
                      Optional
                    </label>
                    <button onClick={() => removeProductFromBundle(bp.productId)} className="text-text-muted hover:text-critical">
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Product picker */}
            <div>
              <button
                type="button"
                onClick={() => setShowProductPicker(!showProductPicker)}
                className="inline-flex items-center gap-2 text-base text-gold hover:text-gold/80"
              >
                <Plus size={16} /> Add Products
              </button>

              {showProductPicker && (
                <div className="mt-3 bg-bg-elevated border border-bg-tertiary rounded-lg p-3">
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    placeholder="Search products to add..."
                    className="input-field text-sm mb-2"
                  />
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {availableProducts.slice(0, 20).map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => addProductToBundle(p)}
                        className="w-full text-left px-3 py-2 rounded text-sm hover:bg-bg-tertiary transition-colors flex items-center justify-between"
                      >
                        <span className="text-text-primary">{p.name}</span>
                        <span className="text-text-muted">T{p.tier}</span>
                      </button>
                    ))}
                    {availableProducts.length === 0 && (
                      <p className="text-sm text-text-muted py-2 text-center">No matching products</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </FormSection>

        {/* Trigger */}
        <FormSection title="Trigger Conditions" id="trigger" expanded={expandedSections.has('trigger')} onToggle={toggleSection}>
          <div className="space-y-4">
            <Field label="Trigger Description">
              <input
                type="text"
                value={bundle.trigger.description}
                onChange={(e) => setBundle((prev) => ({ ...prev, trigger: { ...prev.trigger, description: e.target.value } }))}
                placeholder="e.g. Fasting glucose >100, A1C >5.5"
                className="input-field"
              />
            </Field>
            <Field label="Minimum Match Count">
              <input
                type="number"
                value={bundle.trigger.minimumMatchCount}
                onChange={(e) => setBundle((prev) => ({ ...prev, trigger: { ...prev.trigger, minimumMatchCount: Number(e.target.value) } }))}
                min={1}
                className="input-field w-24"
              />
            </Field>

            {/* Goal conditions */}
            <Field label="Goal Conditions">
              <div className="flex flex-wrap gap-2">
                {HEALTH_GOALS.map((goal) => (
                  <button
                    key={goal}
                    type="button"
                    onClick={() => {
                      const current = bundle.trigger.goalConditions || [];
                      const goals = current.includes(goal)
                        ? current.filter((g) => g !== goal)
                        : [...current, goal];
                      setBundle((prev) => ({
                        ...prev,
                        trigger: { ...prev.trigger, goalConditions: goals },
                      }));
                    }}
                    className={`px-2.5 py-1 rounded text-sm font-medium border transition-colors ${
                      (bundle.trigger.goalConditions || []).includes(goal)
                        ? 'border-gold/50 text-gold bg-gold/10'
                        : 'border-bg-tertiary text-text-muted hover:text-text-secondary'
                    }`}
                  >
                    {goal.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
            </Field>

            {/* Biomarker conditions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-base font-medium text-text-secondary">Biomarker Conditions</label>
                <button type="button" onClick={addTriggerCondition} className="inline-flex items-center gap-1 text-sm text-gold hover:text-gold/80">
                  <Plus size={14} /> Add Condition
                </button>
              </div>
              {bundle.trigger.conditions.length === 0 && (
                <p className="text-sm text-text-muted">No biomarker conditions defined.</p>
              )}
              <div className="space-y-2">
                {bundle.trigger.conditions.map((cond, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-bg-elevated border border-bg-tertiary rounded-lg p-3">
                    <input
                      type="text"
                      value={cond.biomarker}
                      onChange={(e) => updateTriggerCondition(idx, 'biomarker', e.target.value)}
                      placeholder="Biomarker"
                      className="input-field text-sm flex-1"
                    />
                    <select
                      value={cond.operator}
                      onChange={(e) => updateTriggerCondition(idx, 'operator', e.target.value)}
                      className="input-field text-sm w-24"
                    >
                      {OPERATORS.map((op) => (
                        <option key={op} value={op}>{op}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={cond.value}
                      onChange={(e) => updateTriggerCondition(idx, 'value', e.target.value)}
                      placeholder="Value"
                      className="input-field text-sm w-20"
                    />
                    {cond.operator === 'between' && (
                      <input
                        type="number"
                        value={cond.value2 ?? ''}
                        onChange={(e) => updateTriggerCondition(idx, 'value2', Number(e.target.value))}
                        placeholder="Value 2"
                        className="input-field text-sm w-20"
                      />
                    )}
                    <button onClick={() => removeTriggerCondition(idx)} className="text-text-muted hover:text-critical">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </FormSection>

        {/* Safety */}
        <FormSection title="Safety" id="safety" expanded={expandedSections.has('safety')} onToggle={toggleSection}>
          <div className="space-y-4">
            <Field label="Contraindications (comma-separated)">
              <input
                type="text"
                value={bundle.contraindications.join(', ')}
                onChange={(e) => updateField('contraindications', parseArray(e.target.value))}
                placeholder="e.g. pregnancy, liver disease"
                className="input-field"
              />
            </Field>
            <Field label="Warnings (comma-separated)">
              <input
                type="text"
                value={bundle.warnings.join(', ')}
                onChange={(e) => updateField('warnings', parseArray(e.target.value))}
                placeholder="e.g. Monitor liver enzymes"
                className="input-field"
              />
            </Field>
          </div>
        </FormSection>

        {/* Checkout */}
        <FormSection title="Checkout & Upsell" id="checkout" expanded={expandedSections.has('checkout')} onToggle={toggleSection}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={bundle.requiresProvider}
                  onChange={(e) => updateField('requiresProvider', e.target.checked)}
                  className="w-4 h-4 rounded border-bg-tertiary bg-bg-elevated text-accent focus:ring-accent"
                />
                <span className="text-base text-text-secondary">Requires Provider</span>
              </label>
            </div>
            <Field label="Checkout Note">
              <input type="text" value={bundle.checkoutNote || ''} onChange={(e) => updateField('checkoutNote', e.target.value)} placeholder="e.g. Provider sends Rx program link" className="input-field" />
            </Field>
            <Field label="Upsell Path">
              <input type="text" value={bundle.upsellPath || ''} onChange={(e) => updateField('upsellPath', e.target.value)} placeholder="Description of upsell" className="input-field" />
            </Field>
            <Field label="Upsell Bundle ID">
              <input type="text" value={bundle.upsellBundleId || ''} onChange={(e) => updateField('upsellBundleId', e.target.value)} placeholder="e.g. hormonal-optimization" className="input-field" />
            </Field>
          </div>
        </FormSection>

        {/* Meta */}
        <FormSection title="Meta" id="meta" expanded={expandedSections.has('meta')} onToggle={toggleSection}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Sort Order">
              <input type="number" value={bundle.sortOrder} onChange={(e) => updateField('sortOrder', Number(e.target.value))} className="input-field" />
            </Field>
            <Field label="Updated By">
              <input type="text" value={bundle.updatedBy} onChange={(e) => updateField('updatedBy', e.target.value)} placeholder="admin" className="input-field" />
            </Field>
            {bundle.updatedAt && (
              <Field label="Last Updated" className="sm:col-span-2">
                <p className="text-base text-text-muted py-2">{new Date(bundle.updatedAt).toLocaleString()}</p>
              </Field>
            )}
          </div>
        </FormSection>
      </div>

      {/* Bottom save bar */}
      <div className="sticky bottom-0 mt-6 py-4 bg-bg-deep border-t border-bg-tertiary flex items-center justify-end gap-3">
        <button onClick={() => navigate('/admin/bundles')} className="px-4 py-2.5 text-base text-text-secondary hover:text-text-primary transition-colors">
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-accent hover:bg-accent/80 text-white rounded-lg text-base font-medium transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Save Bundle
        </button>
      </div>
    </div>
  );
}

// --- Reusable sub-components ---

function FormSection({
  title,
  id,
  expanded,
  onToggle,
  children,
}: {
  title: string;
  id: string;
  expanded: boolean;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-bg-elevated transition-colors"
      >
        <h3 className="text-base font-semibold text-text-secondary uppercase tracking-wider">{title}</h3>
        {expanded ? <ChevronUp size={16} className="text-text-muted" /> : <ChevronDown size={16} className="text-text-muted" />}
      </button>
      {expanded && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}

function Field({
  label,
  required,
  className,
  children,
}: {
  label: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-text-muted mb-1.5">
        {label}
        {required && <span className="text-accent ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}
